import { TableColumnGroups } from "~/constants/tableColumnGroups"
import { getColumnNaming } from "~/components/planning/ProductTable/utils/columnUtils"
import { ExtraDataTable } from "~/utils/dynamicTableUtils"
import { replaceName } from "~/utils/namingUtils"
import { rankOnSearchString } from "~/utils/search"
import { isStorageLocationColId } from "~/utils/storageLocationColumns"
import { groupBy } from "~/utils/tsUtils"

/** One checkbox in the column chooser, with its label already resolved. */
export type ChooserColumn = {
  id: string
  displayName: string
  description?: string
  group: string
  keyWords?: string[]
}

/** A caller's metadata for a column; its label comes from the title resolver. */
export type ChooserColumnMeta = Omit<ChooserColumn, "displayName">

export type RankedColumn = ChooserColumn & { rank: number }

export type ChooserGroup = { group: string; columns: RankedColumn[]; bestRank: number }

export const EXTRA_DATA_SOURCES = [
  {
    suffix: "(extradata)",
    table: ExtraDataTable.SKU,
    group: TableColumnGroups.ExternalData,
    tooltip: "Dynamic field generated from the SKUs 'ExtraData'",
  },
  {
    suffix: "(collection-extradata)",
    table: ExtraDataTable.COLLECTION_ON_SKU,
    group: TableColumnGroups.ExternalCollectionData,
    tooltip: "Dynamic field generated from the Collections 'ExtraData'",
  },
] as const

export type ExtraDataSource = (typeof EXTRA_DATA_SOURCES)[number]

export const getExtraDataSource = (id: string): ExtraDataSource | undefined =>
  EXTRA_DATA_SOURCES.find((source) => id.includes(source.suffix))

export const EXTERNAL_GROUPS: ReadonlySet<string> = new Set(EXTRA_DATA_SOURCES.map((s) => s.group))

const stripExtraDataSuffix = (id: string) => {
  const source = getExtraDataSource(id)
  return source ? id.replace(source.suffix, "") : id
}

/** "incomingStockBeforeEta" → "Incoming Stock Before Eta": the label of last resort. */
export const formatColumnIdAsTitle = (id: string) =>
  id
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace("-", " ")
    .replace(/^\w/, (c) => c.toUpperCase())

export type TitleSources = {
  /** The table's own labels; they win over the naming registry. */
  overrides?: (id: string) => string | undefined
  /** Used when neither an override nor the registry names the column, e.g. a grid `headerName`. */
  fallbacks?: Readonly<Record<string, string>>
  /** Formats the id when nothing else names the column. */
  format?: (id: string) => string
}

/**
 * One label lookup for every non-extradata column, first hit wins: the table's overrides, the
 * naming registry, the fallbacks, then the formatted id.
 */
export function createTitleResolver({
  overrides,
  fallbacks,
  format = formatColumnIdAsTitle,
}: TitleSources): (id: string) => string {
  return (id) =>
    replaceName(
      overrides?.(id) ?? getColumnNaming(id)?.columnChooserTitle ?? fallbacks?.[id] ?? format(id),
    )
}

function otherColumnGroup(id: string, otherGroup: string) {
  return (
    getColumnNaming(id)?.group ??
    (isStorageLocationColId(id) ? TableColumnGroups.AvailabilityAndInventory : otherGroup)
  )
}

/**
 * Every column the chooser offers, in group-appearance order: the caller's curated columns, then
 * the table's other columns (described from the naming registry), then SKU/collection extradata.
 * `columnIds` is what the table offers, already without columns the chooser must not list.
 */
export function buildChooserColumns({
  columnIds,
  curated,
  resolveTitle,
  extraDataTitle,
  otherGroup = TableColumnGroups.Other,
  isHiddenOther,
}: {
  columnIds: readonly string[]
  curated: readonly ChooserColumnMeta[]
  resolveTitle: (id: string) => string
  extraDataTitle: (field: string, table: ExtraDataTable) => string
  otherGroup?: string
  /** Hides a non-curated, non-extradata column, e.g. one gated off by a feature flag. */
  isHiddenOther?: (id: string) => boolean
}): ChooserColumn[] {
  const offered = new Set(columnIds)
  const curatedIds = new Set(curated.map((column) => column.id))

  const curatedColumns = curated
    .filter((column) => offered.has(column.id))
    .map(({ id, description, group, keyWords }) => ({
      id,
      displayName: resolveTitle(id),
      description,
      group,
      keyWords,
    }))

  const rest = columnIds.filter((id) => !curatedIds.has(id) && id !== "rightMargin")

  const otherColumns = rest
    .filter((id) => !getExtraDataSource(id) && !isHiddenOther?.(id))
    .map((id): ChooserColumn => {
      const naming = getColumnNaming(id)
      return {
        id,
        displayName: resolveTitle(id),
        description: naming?.tooltip ?? "",
        keyWords: naming?.keyWords,
        group: otherColumnGroup(id, otherGroup),
      }
    })

  const extraDataColumns = rest.flatMap((id): ChooserColumn[] => {
    const source = getExtraDataSource(id)
    if (!source) return []
    return [
      {
        id,
        displayName: extraDataTitle(id.replace(source.suffix, ""), source.table),
        group: source.group,
      },
    ]
  })

  return [...curatedColumns, ...otherColumns, ...extraDataColumns]
}

/** Rank 0 = the label matches, 1 = label, id and keywords together do, 2 = only description/group. */
const SEARCH_RANK_TIERS = [["displayName"], ["displayName", "id", "keyWords"]] as const

function compareColumns(a: RankedColumn, b: RankedColumn) {
  return a.rank - b.rank || a.displayName.localeCompare(b.displayName, undefined, { numeric: true })
}

/**
 * Columns matching `searchText`, grouped, each group sorted by rank then label (numbers in natural
 * order). While searching, the group holding the best match comes first.
 */
export function buildChooserGroups(
  columns: readonly ChooserColumn[],
  searchText: string,
): ChooserGroup[] {
  // The "(extradata)" suffix is not searchable, or that text would match every extradata column.
  const ranked = rankOnSearchString(columns, searchText, SEARCH_RANK_TIERS, (column) => ({
    ...column,
    id: stripExtraDataSuffix(column.id),
  })).map(({ item, rank }): RankedColumn => ({ ...item, rank }))
  const groups = Object.entries(groupBy(ranked, "group")).map(
    ([group, groupColumns]): ChooserGroup => {
      const sorted = [...groupColumns].sort(compareColumns)
      return { group, columns: sorted, bestRank: sorted[0]?.rank ?? 0 }
    },
  )
  if (searchText) groups.sort((a, b) => a.bestRank - b.bestRank)
  return groups
}

/** Each column's grid `headerName`, for labelling columns no registry or caller label covers. */
export function headerNamesByColId(
  columns: readonly { colId?: string; headerName?: unknown }[] | undefined,
): Record<string, string> {
  const titles: Record<string, string> = {}
  for (const column of columns ?? []) {
    if (column.colId && typeof column.headerName === "string") {
      titles[column.colId] = column.headerName
    }
  }
  return titles
}

/** What closing the chooser changed, for the "Column Chooser Closed" event. */
export function diffColumnIds(before: readonly string[], after: readonly string[]) {
  const beforeSet = new Set(before)
  const afterSet = new Set(after)
  return {
    added: after.filter((id) => !beforeSet.has(id)),
    removed: before.filter((id) => !afterSet.has(id)),
  }
}
